import {
  getDhValidDddsForFastchipLines,
  getBaValidDdds,
  getHdm2ValidDdds,
  isDhMarketEstado,
} from '../../utils/fastchipClusterRules';
import { isDhContractToken } from '../../utils/contractClusterTokens';

/**
 * Utilitário para validação de DDDs baseado no estado do cliente e opção Fastchip.
 * Regras Fastchip seguem clusters de ICCIDs disponíveis.
 */

/**
 * Cidades do Cluster MH (Guarapuava DDD 42 – PR).
 * Nomes em maiúsculas sem acentos para facilitar comparação normalizada.
 */
export const MH_CITIES: string[] = [
  'GUARAPUAVA', 'UNIAO DA VITORIA', 'PRUDENTOPOLIS', 'PITANGA',
  'LARANJEIRAS DO SUL', 'PINHAO', 'CRUZ MACHADO', 'BITURUNA',
  'CANDOI', 'PALMITAL', 'GENERAL CARNEIRO', 'TURVO', 'CANTAGALO',
  'RIO BONITO DO IGUACU', 'NOVA LARANJEIRAS', 'SANTA MARIA DO OESTE',
  'GUAMIRANGA', 'RESERVA DO IGUACU', 'GOIOXIM', 'BOA VENTURA DE SAO ROQUE',
  'LARANJAL', 'FOZ DO JORDAO', 'MARQUINHO', 'PORTO VITORIA',
  'CAMPINA DO SIMAO', 'VIRMOND', 'MATO RICO', 'PORTO BARREIRO',
];

/** Normaliza nome de cidade para comparação (maiúsculas, sem acentos). */
export const normalizeCityName = (city: string): string =>
  city.toUpperCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '').trim();

export const getValidDdds = (
  estado: string,
  isFastchip: boolean,
  cluster?: string,
  saleToken?: string
): string[] => {
  // Mapeamento de DDDs por estado (regras normais - SEM Fastchip)
  const stateDddMap: { [key: string]: string[] } = {
    'AC': ['68'],
    'AL': ['82'],
    'AP': ['96'],
    'AM': ['92', '97'],
    'BA': ['71', '73', '74', '75', '77'],
    'CE': ['85', '88'],
    'DF': ['61'],
    'ES': ['27', '28'],
    'GO': ['62', '64'],
    'MA': ['98', '99'],
    'MT': ['65', '66'],
    'MS': ['67'],
    'MG': ['31', '32', '33', '34', '35', '37', '38'],
    'PA': ['91', '93', '94'],
    'PB': ['83'],
    'PR': ['41', '42', '43', '44', '45', '46'],
    'PE': ['81', '87'],
    'PI': ['86', '89'],
    'RJ': ['21', '22', '24'],
    'RN': ['84'],
    'RS': ['51', '52', '53', '54', '55'],
    'RO': ['69'],
    'RR': ['95'],
    'SC': ['47', '48', '49'],
    'SP': ['11', '12', '13', '14', '15', '16', '17', '18', '19'],
    'SE': ['79'],
    'TO': ['63']
  };

  // Fastchip + token DH: pool de linha (MG 31–38), inclusive cliente RJ/ES.
  // Por entrega ignora o token e usa stateDddMap da UF.
  if (isFastchip && isDhContractToken(saleToken)) {
    return getDhValidDddsForFastchipLines();
  }

  // Se não tem estado, retorna array vazio
  if (!estado) {
    return [];
  }

  const estadoUpper = estado.toUpperCase();
  const dddNormal = stateDddMap[estadoUpper] || [];

  // ✅ REGRAS ESPECÍFICAS PARA FASTCHIP (baseadas em clusters de ICCIDs)
  if (isFastchip) {
    if (isDhMarketEstado(estadoUpper)) {
      return getDhValidDddsForFastchipLines();
    }

    switch (estadoUpper) {
      // Cluster Sul - Mantêm seus próprios DDDs
      case 'SC':
        return ['47', '48', '49'];
      case 'SP':
        return ['11', '12', '13', '14', '15', '16', '17', '18', '19'];
      case 'PR':
        if (cluster === 'MH') return ['42'];
        return ['41', '42', '43', '44', '45'];

      // Cluster HB (PE/CE)
      case 'PE':
      case 'CE':
        return ['81', '85', '87', '88'];

      // Cluster CP (PI/AL/RN/PB)
      case 'PI':
      case 'AL':
      case 'RN':
      case 'PB':
        return ['82', '83', '84', '86', '89'];

      // Cluster TPCL Fastchip (núcleo DF/GO/TO/MT/MS/AC/RO — DDDs 61–69)
      case 'DF':
      case 'GO':
      case 'TO':
      case 'MT':
      case 'MS':
      case 'AC':
      case 'RO':
        return dddNormal;

      case 'BA':
        return getBaValidDdds();

      case 'RS':
        return getHdm2ValidDdds();

      // Outros estados - Usam seus próprios DDDs
      default:
        return dddNormal;
    }
  }

  // Para contratos não-fastchip, retorna DDDs normais do estado
  return dddNormal;
};

/**
 * Função para obter DDDs válidos considerando disponibilidade de ICCIDs (APENAS para Fastchip)
 * Similar ao getValidDddsWithICcIdFallback do ContractsPage
 */
export const getValidDddsForFastchip = (
  estado: string, 
  isFastchip: boolean, 
  hasAvailableICcIds: boolean
): string[] => {
  // Se NÃO é fastchip, retornar DDDs normais do estado
  if (!isFastchip) {
    return getValidDdds(estado, false);
  }
  
  // Se É fastchip mas NÃO há ICCIDs disponíveis, retornar array vazio
  if (isFastchip && !hasAvailableICcIds) {
    return [];
  }
  
  // Se É fastchip E há ICCIDs disponíveis, retornar DDDs com regras de cluster
  return getValidDdds(estado, true);
};
